// Condo Unit Detail mock page - all data is hard-coded for UI only.
// Backend developers will replace these constants with live data.
import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "../../components/mui";
import { ApartmentRoundedIcon, ChevronLeftRoundedIcon, AddRoundedIcon, SwapVertRoundedIcon, EditRoundedIcon, SettingsRoundedIcon, VisibilityRoundedIcon } from "../../components/icons";

import "../../styles/main.css";
import "../../styles/pages/condoUnitDetail.css";
import PageLayout from "../../components/shared/PageLayout";
import CondoUnitFormModal from "./modals/AddCondoUnitModal";
import ContractFormModal from "./modals/ContractFormModal";
import CondoUnitInfoCard from "./components/CondoUnitInfoCard";
import ManageMiscExpenseModal from "./modals/ManageMiscExpenseModal";
import ManageUtilitiesModal from "./modals/ManageUtilitiesModal";
import ManageInventoryModal from "./modals/ManageInventoryModal";
import ManageMaintenanceModal from "./modals/ManageMaintenanceModal";
import ManageAttachmentModal from "./modals/ManageAttachmentModal";
import RentalPaymentFormModal from "./modals/RentalPaymentFormModal";
import MiscPaymentFormModal from "../condo-payment/modals/MiscPaymentFormModal";
import UtilityPaymentFormModal from "../condo-payment/modals/UtilityPaymentFormModal";
import AllowanceFormModal from "../condo-payment/modals/AllowanceFormModal";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import { useToast } from "../../components/shared/ToastProvider";
import {
  OVERVIEW_ITEMS,
  CONTRACTS,
  INVENTORY_ROWS,
  DETAIL_TABLES,
  MISC_EXPENSE_ROWS,
  RENTAL_PAYMENT_ROWS,
  MISC_PAYMENT_ROWS,
  UTILITY_PAYMENT_ROWS,
  MAINTENANCE_ROWS,
  ALLOWANCE_ROWS,
  ATTACHMENT_ROWS,
  UTILITIES_ROWS
} from "./condoUnitDetailMocks";

function DetailSection({ title, actions, children }) {
  return (
    <Box className="condo-unit-detail-section">
      <Box className="condo-unit-detail-section-header">
        <Typography
          variant="subtitle1"
          className="card-title"
        >
          {title}
        </Typography>
        {actions ? <div className="condo-unit-detail-section-actions">{actions}</div> : null}
      </Box>
      {children}
    </Box>
  );
}

function DetailGrid({ items, columns = 4 }) {
  return (
    <Box className={`condo-unit-detail-grid condo-unit-detail-grid--cols-${columns}`}>
      {items.map((item) => (
        <Box className="condo-unit-detail-item" key={`${item.label}-${item.value}`}>
          <Typography variant="caption" className="condo-unit-detail-item-label">
            {item.label}
          </Typography>
          {item.type === "chip" ? (
            <Chip
              size="small"
              label={item.value}
              color={item.color || "default"}
              className="condo-unit-detail-chip"
            />
          ) : (
            <Typography variant="body2" className="condo-unit-detail-item-value">
              {item.value || "—"}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}


function DetailTableSection({
  id,
  title,
  columns,
  manageLabel,
  onManage,
  onRentalEdit,
  onMiscPaymentEdit,
  onMiscPaymentDelete,
  onUtilityPaymentEdit,
  onUtilityPaymentDelete,
  onAllowanceEdit,
  onAllowanceDelete
}) {
  return (
    <DetailSection
      title={title}
      actions={
        manageLabel ? (
          <Button
            variant="outlined"
            size="small"
            className="condo-unit-detail-action-button"
            startIcon={
              manageLabel.startsWith("Add")
                ? <AddRoundedIcon />
                : manageLabel === "View Rental Payment Dues"
                ? <VisibilityRoundedIcon />
                : <SettingsRoundedIcon />
            }
            onClick={onManage}
          >
            {manageLabel === "Manage Rental Payments" ? "Edit Rental Payments" : manageLabel}
          </Button>
        ) : null
      }
    >
      <TableContainer className="condo-unit-detail-table-container">
        <Table size="small" className="condo-unit-detail-table" aria-labelledby={id}>
          <TableHead className="condo-unit-detail-table-head">
            <TableRow>
              {(id === "rental-payments" || id === "misc-payments" || id === "utility-payments" || id === "allowance") && (
                <TableCell className="condo-unit-detail-edit-cell" />
              )}
              {columns.map((column) => (
                <TableCell key={`${id}-${column}`}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {id === "misc-expenses" && (
              MISC_EXPENSE_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell>{row.type}</TableCell>
                  <TableCell className="condo-unit-detail-amount-cell">{row.amount}</TableCell>
                  <TableCell>{row.startDate}</TableCell>
                  <TableCell>{row.payableTo || ""}</TableCell>
                  <TableCell>{row.inCharge || ""}</TableCell>
                  <TableCell>{row.remarks || ""}</TableCell>
                </TableRow>
              ))
            )}

            {id === "utilities" && (
              UTILITIES_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.accountName}</TableCell>
                  <TableCell>{row.accountNo}</TableCell>
                  <TableCell className="condo-unit-detail-amount-cell">{row.amount}</TableCell>
                  <TableCell>{row.startDate}</TableCell>
                  <TableCell>{row.payableTo || ""}</TableCell>
                  <TableCell>{row.inCharge || ""}</TableCell>
                  <TableCell>{row.remarks || ""}</TableCell>
                </TableRow>
              ))
            )}

            {id === "attachments" && (
              ATTACHMENT_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell>{row.file}</TableCell>
                  <TableCell>{row.dateCreated}</TableCell>
                </TableRow>
              ))
            )}

            {id === "rental-payments" && (
              RENTAL_PAYMENT_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell className="condo-unit-detail-edit-cell">
                    <Button
                      variant="text"
                      size="small"
                      onClick={onRentalEdit ? () => onRentalEdit(row) : undefined}
                    >
                      Edit
                    </Button>
                  </TableCell>
                  <TableCell>{row.acctEntryDate}</TableCell>
                  <TableCell>{row.periodCovered}</TableCell>
                  <TableCell>{row.receiptNo}</TableCell>
                  <TableCell>{row.paymentDate}</TableCell>
                  <TableCell>{row.rfNo}</TableCell>
                  <TableCell>{row.checkNo}</TableCell>
                  <TableCell
                    className={
                      `condo-unit-detail-amount-cell ` +
                      (row.status === "credit"
                        ? "condo-unit-detail-amount-positive"
                        : row.status === "debit"
                        ? "condo-unit-detail-amount-negative"
                        : "")
                    }
                  >
                    {row.amount}
                  </TableCell>
                  <TableCell>{row.remarks}</TableCell>
                </TableRow>
              ))
            )}

            {id === "rental-payments" && (
              <TableRow className="condo-unit-detail-total-row">
                <TableCell className="condo-unit-detail-total-label-cell" />
                <TableCell colSpan={6} />
                <TableCell className="condo-unit-detail-amount-cell condo-unit-detail-amount-total">
                  <Typography component="div">
                    <span className="condo-unit-detail-total-inline-label">TOTAL</span>
                    <span className="condo-unit-detail-total-amount">PHP 560,000.00</span>
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            )}

            {id === "misc-payments" && (
              MISC_PAYMENT_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell className="condo-unit-detail-edit-cell">
                    <Button
                      variant="text"
                      size="small"
                      onClick={onMiscPaymentEdit ? () => onMiscPaymentEdit(row) : undefined}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={onMiscPaymentDelete ? () => onMiscPaymentDelete(row) : undefined}
                    >
                      Delete
                    </Button>
                  </TableCell>
                  <TableCell>{row.acctEntryDate}</TableCell>
                  <TableCell>{row.periodCovered}</TableCell>
                  <TableCell>{row.paymentDate}</TableCell>
                  <TableCell>{row.expenseType}</TableCell>
                  <TableCell className="condo-unit-detail-amount-cell">{row.amount}</TableCell>
                  <TableCell>{row.rfNo}</TableCell>
                  <TableCell>{row.checkNo}</TableCell>
                  <TableCell>{row.payableTo}</TableCell>
                  <TableCell>{row.condoCompany}</TableCell>
                  <TableCell>{row.condoSharing}</TableCell>
                  <TableCell>{row.remarks}</TableCell>
                </TableRow>
              ))
            )}

            {id === "misc-payments" && (
              <TableRow className="condo-unit-detail-total-row">
                <TableCell className="condo-unit-detail-total-label-cell" />
                {/* span from Acct Entry Date up to (but not including) Amount */}
                <TableCell colSpan={4} />
                <TableCell className="condo-unit-detail-amount-cell condo-unit-detail-amount-total">
                  <Typography component="div">
                    <span className="condo-unit-detail-total-inline-label">TOTAL</span>
                    <span className="condo-unit-detail-total-amount">PHP 182,963.94</span>
                  </Typography>
                </TableCell>
                {/* remaining columns after Amount */}
                <TableCell colSpan={6} />
              </TableRow>
            )}

            {id === "utility-payments" && (
              UTILITY_PAYMENT_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell className="condo-unit-detail-edit-cell">
                    <Button
                      variant="text"
                      size="small"
                      onClick={onUtilityPaymentEdit ? () => onUtilityPaymentEdit(row) : undefined}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={onUtilityPaymentDelete ? () => onUtilityPaymentDelete(row) : undefined}
                    >
                      Delete
                    </Button>
                  </TableCell>
                  <TableCell>{row.acctEntryDate}</TableCell>
                  <TableCell>{row.periodCovered}</TableCell>
                  <TableCell>{row.paymentDate}</TableCell>
                  <TableCell>{row.utilityType}</TableCell>
                  <TableCell className="condo-unit-detail-amount-cell">{row.amount}</TableCell>
                  <TableCell>{row.rfNo}</TableCell>
                  <TableCell>{row.checkNo}</TableCell>
                  <TableCell>{row.payableTo}</TableCell>
                  <TableCell>{row.condoCompany}</TableCell>
                  <TableCell>{row.condoSharing}</TableCell>
                  <TableCell>{row.remarks}</TableCell>
                </TableRow>
              ))
            )}

            {id === "utility-payments" && (
              <TableRow className="condo-unit-detail-total-row">
                <TableCell className="condo-unit-detail-total-label-cell" />
                {/* span from Acct Entry Date up to (but not including) Amount */}
                <TableCell colSpan={4} />
                <TableCell className="condo-unit-detail-amount-cell condo-unit-detail-amount-total">
                  <Typography component="div">
                    <span className="condo-unit-detail-total-inline-label">TOTAL</span>
                    <span className="condo-unit-detail-total-amount">PHP 100,464.25</span>
                  </Typography>
                </TableCell>
                {/* remaining columns after Amount */}
                <TableCell colSpan={6} />
              </TableRow>
            )}

            {id === "maintenance" && (
              MAINTENANCE_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{row.inCharge}</TableCell>
                  <TableCell className="condo-unit-detail-numeric-cell">{row.total}</TableCell>
                  <TableCell>{row.acctEntryDate}</TableCell>
                </TableRow>
              ))
            )}

            {id === "allowance" && (
              ALLOWANCE_ROWS.map((row, index) => (
                <TableRow key={`${id}-row-${index}`}>
                  <TableCell className="condo-unit-detail-edit-cell">
                    <Button
                      variant="text"
                      size="small"
                      onClick={onAllowanceEdit ? () => onAllowanceEdit(row) : undefined}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={onAllowanceDelete ? () => onAllowanceDelete(row) : undefined}
                    >
                      Delete
                    </Button>
                  </TableCell>
                  <TableCell>{row.payment}</TableCell>
                  <TableCell>{row.payableTo}</TableCell>
                  <TableCell>{row.remarks}</TableCell>
                  <TableCell className="condo-unit-detail-amount-cell">{row.amount}</TableCell>
                  <TableCell>{row.condoCompany}</TableCell>
                  <TableCell>{row.condoSharing}</TableCell>
                </TableRow>
              ))
            )}

            {id !== "misc-expenses" && id !== "utilities" && id !== "attachments" &&
             id !== "rental-payments" && id !== "misc-payments" && id !== "utility-payments" &&
             id !== "maintenance" && id !== "allowance" && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  className="condo-unit-detail-empty-cell"
                >
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DetailSection>
  );
}

export default function CondoUnitDetail({ onLogout, onNavigate }) {
  const [activeTab, setActiveTab] = React.useState("info");
  const [showOtherContracts, setShowOtherContracts] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = React.useState(false);
  const [contractModalMode, setContractModalMode] = React.useState("add"); // "add" | "edit"
  const [isManageMiscModalOpen, setIsManageMiscModalOpen] = React.useState(false);
  const [isManageUtilitiesModalOpen, setIsManageUtilitiesModalOpen] = React.useState(false);
  const [isManageInventoryModalOpen, setIsManageInventoryModalOpen] = React.useState(false);
  const [isManageAttachmentModalOpen, setIsManageAttachmentModalOpen] = React.useState(false);
  const [isManageMaintenanceModalOpen, setIsManageMaintenanceModalOpen] = React.useState(false);
  const [isRentalPaymentModalOpen, setIsRentalPaymentModalOpen] = React.useState(false);
  const [selectedRentalPayment, setSelectedRentalPayment] = React.useState(null);
  const [isMiscPaymentModalOpen, setIsMiscPaymentModalOpen] = React.useState(false);
  const [miscPaymentModalMode, setMiscPaymentModalMode] = React.useState("add");
  const [selectedMiscPayment, setSelectedMiscPayment] = React.useState(null);
  const [isMiscPaymentDeleteOpen, setIsMiscPaymentDeleteOpen] = React.useState(false);
  const [miscPaymentToDelete, setMiscPaymentToDelete] = React.useState(null);
  const [isUtilityPaymentModalOpen, setIsUtilityPaymentModalOpen] = React.useState(false);
  const [utilityPaymentModalMode, setUtilityPaymentModalMode] = React.useState("add");
  const [selectedUtilityPayment, setSelectedUtilityPayment] = React.useState(null);
  const [isUtilityPaymentDeleteOpen, setIsUtilityPaymentDeleteOpen] = React.useState(false);
  const [utilityPaymentToDelete, setUtilityPaymentToDelete] = React.useState(null);
  const [isAllowanceModalOpen, setIsAllowanceModalOpen] = React.useState(false);
  const [allowanceModalMode, setAllowanceModalMode] = React.useState("add");
  const [selectedAllowance, setSelectedAllowance] = React.useState(null);
  const [isAllowanceDeleteOpen, setIsAllowanceDeleteOpen] = React.useState(false);
  const [allowanceToDelete, setAllowanceToDelete] = React.useState(null);
  const { showToast } = useToast();

  const handleBack = () => {
    if (typeof onNavigate === "function") {
      onNavigate({ key: "condo-units" });
    }
  };

  const handleMiscPaymentDelete = (row) => {
    setMiscPaymentToDelete(row);
    setIsMiscPaymentDeleteOpen(true);
  };

  const handleConfirmMiscPaymentDelete = () => {
    setIsMiscPaymentDeleteOpen(false);
    setMiscPaymentToDelete(null);
    if (showToast) {
      showToast({ message: "Misc expense payment deleted", severity: "success" });
    }
  };

  const handleUtilityPaymentDelete = (row) => {
    setUtilityPaymentToDelete(row);
    setIsUtilityPaymentDeleteOpen(true);
  };

  const handleConfirmUtilityPaymentDelete = () => {
    setIsUtilityPaymentDeleteOpen(false);
    setUtilityPaymentToDelete(null);
    if (showToast) {
      showToast({ message: "Utility payment deleted", severity: "success" });
    }
  };

  const handleAllowanceDelete = (row) => {
    setAllowanceToDelete(row);
    setIsAllowanceDeleteOpen(true);
  };

  const handleConfirmAllowanceDelete = () => {
    setIsAllowanceDeleteOpen(false);
    setAllowanceToDelete(null);
    if (showToast) {
      showToast({ message: "Allowance deleted", severity: "success" });
    }
  };

  return (
    <PageLayout activeKey="condo-units" onLogout={onLogout} onNavigate={onNavigate} className="page-content">
      <Box className="condo-unit-detail-header-actions">
        <Button
          variant="text"
          startIcon={<ChevronLeftRoundedIcon />}
          className="condo-unit-detail-action-button"
          onClick={handleBack}
        >
          Back to list
        </Button>
      </Box>

      {/* Primary container: Condo Unit Info only */}
      <Paper elevation={0} className="content-box condo-unit-detail-card">
        <CondoUnitInfoCard
          title="Condo Unit Info"
          items={OVERVIEW_ITEMS}
          showEdit
          onEdit={() => setIsEditModalOpen(true)}
        />
      </Paper>

      {/* Tabs strip (outside of container, reusing shared tabs-container styles like Condo Payment) */}
      <Box className="tabs-container">
        <Tabs
          value={activeTab}
          aria-label="Condo unit detail tabs"
          onChange={(_, value) => setActiveTab(value)}
        >
          <Tab value="info" label="Info" />
          <Tab value="payments" label="Payments" />
        </Tabs>
      </Box>

      {activeTab === "info" && (
        <>
          {/* Contract Info container */}
          <Paper elevation={0} className="content-box condo-unit-detail-card">
            <DetailSection
              title="Contract Info"
              actions={
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    className="condo-unit-detail-action-button"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => {
                      setContractModalMode("add");
                      setIsContractModalOpen(true);
                    }}
                  >
                    Add contract
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    className="condo-unit-detail-action-button"
                    startIcon={<SwapVertRoundedIcon />}
                    onClick={() => setShowOtherContracts((prev) => !prev)}
                  >
                    {showOtherContracts ? "Hide Other Contracts" : "Show Other Contracts"}
                  </Button>
                </Stack>
              }
            >
              {(showOtherContracts ? CONTRACTS : CONTRACTS.slice(0, 1)).map((contract) => (
                <Box key={contract.id} className="condo-unit-contract-block">
                  <Box className="condo-unit-contract-header">
                    <Typography variant="subtitle2" className="condo-unit-contract-label">
                      {contract.label}
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<EditRoundedIcon fontSize="small" />}
                      onClick={() => {
                        setContractModalMode("edit");
                        setIsContractModalOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Box>

                  <Box className="condo-unit-contract-grid">
                    {['rental', 'security', 'advance', 'lessor'].map((group) => (
                      <Box key={group} className="condo-unit-contract-column">
                        {contract.fields
                          .filter((item) => item.group === group)
                          .map((item) => (
                            <Box className="condo-unit-contract-item" key={`${group}-${item.label}-${item.value}`}>
                              <Typography variant="caption" className="condo-unit-contract-label-text">
                                {item.label}:
                              </Typography>
                              {item.type === "chip" ? (
                                <Chip
                                  size="small"
                                  label={item.value}
                                  color={item.color || "default"}
                                  className="condo-unit-detail-chip"
                                />
                              ) : (
                                <Typography variant="body2" className="condo-unit-contract-value">
                                  {item.value || "—"}
                                </Typography>
                              )}
                            </Box>
                          ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </DetailSection>
          </Paper>

          {/* Detail tables containers: Active Misc Expenses, Active Utilities, Inventory, Attachments */}
          {DETAIL_TABLES.map((table) => (
            <Paper
              key={table.id}
              elevation={0}
              className="content-box condo-unit-detail-card"
            >
              <DetailTableSection
                {...table}
                onManage={
                  table.id === "misc-expenses"
                    ? () => setIsManageMiscModalOpen(true)
                    : table.id === "utilities"
                    ? () => setIsManageUtilitiesModalOpen(true)
                    : table.id === "inventory"
                    ? () => setIsManageInventoryModalOpen(true)
                    : table.id === "attachments"
                    ? () => setIsManageAttachmentModalOpen(true)
                    : undefined
                }
              />
            </Paper>
          ))}
        </>
      )}

      {/* Edit Condo Unit modal (reuses CondoUnitFormModal content) */}
      <CondoUnitFormModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Condo Unit"
        primaryActionLabel="Update"
      />

      {/* Add/Edit Contract modal (shared for both modes) */}
      <ContractFormModal
        open={isContractModalOpen}
        onClose={() => setIsContractModalOpen(false)}
        title={contractModalMode === "edit" ? "Edit Contract" : "Add Contract"}
        primaryActionLabel={contractModalMode === "edit" ? "Update" : "Create"}
      />

      {/* Manage Misc Expense modal */}
      <ManageMiscExpenseModal
        open={isManageMiscModalOpen}
        onClose={() => setIsManageMiscModalOpen(false)}
        condoItems={OVERVIEW_ITEMS}
        rows={MISC_EXPENSE_ROWS}
      />

      {/* Manage Utilities modal */}
      <ManageUtilitiesModal
        open={isManageUtilitiesModalOpen}
        onClose={() => setIsManageUtilitiesModalOpen(false)}
        condoItems={OVERVIEW_ITEMS}
        rows={UTILITIES_ROWS}
      />

      {/* Manage Inventory modal */}
      <ManageInventoryModal
        open={isManageInventoryModalOpen}
        onClose={() => setIsManageInventoryModalOpen(false)}
        condoItems={OVERVIEW_ITEMS}
        rows={INVENTORY_ROWS}
      />

      {/* Manage Maintenance modal */}
      <ManageMaintenanceModal
        open={isManageMaintenanceModalOpen}
        onClose={() => setIsManageMaintenanceModalOpen(false)}
        condoItems={OVERVIEW_ITEMS}
        rows={MAINTENANCE_ROWS}
      />

      {/* Manage Attachment modal */}
      <ManageAttachmentModal
        open={isManageAttachmentModalOpen}
        onClose={() => setIsManageAttachmentModalOpen(false)}
        attachments={ATTACHMENT_ROWS}
      />

      {/* Edit Rental Payment modal (UI-only) */}
      <RentalPaymentFormModal
        open={isRentalPaymentModalOpen}
        onClose={() => setIsRentalPaymentModalOpen(false)}
        mode="edit"
        condoItems={OVERVIEW_ITEMS}
        initialValues={selectedRentalPayment || {}}
      />

      {/* Add/Edit Misc Expense Payment modal (UI-only) */}
      <MiscPaymentFormModal
        open={isMiscPaymentModalOpen}
        onClose={() => setIsMiscPaymentModalOpen(false)}
        mode={miscPaymentModalMode}
        condoItems={OVERVIEW_ITEMS}
        initialValues={selectedMiscPayment || {}}
      />

      <ConfirmDialog
        open={isMiscPaymentDeleteOpen}
        onClose={() => setIsMiscPaymentDeleteOpen(false)}
        onConfirm={handleConfirmMiscPaymentDelete}
        title="Delete Record"
        message="Are you sure you want to delete this misc expense payment? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Add/Edit Utility Payment modal (UI-only) */}
      <UtilityPaymentFormModal
        open={isUtilityPaymentModalOpen}
        onClose={() => setIsUtilityPaymentModalOpen(false)}
        mode={utilityPaymentModalMode}
        initialValues={selectedUtilityPayment || {}}
      />

      <ConfirmDialog
        open={isUtilityPaymentDeleteOpen}
        onClose={() => setIsUtilityPaymentDeleteOpen(false)}
        onConfirm={handleConfirmUtilityPaymentDelete}
        title="Delete Record"
        message="Are you sure you want to delete this utility payment? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Add/Edit Allowance modal (UI-only) */}
      <AllowanceFormModal
        open={isAllowanceModalOpen}
        onClose={() => setIsAllowanceModalOpen(false)}
        mode={allowanceModalMode}
        initialValues={selectedAllowance || {}}
      />

      <ConfirmDialog
        open={isAllowanceDeleteOpen}
        onClose={() => setIsAllowanceDeleteOpen(false)}
        onConfirm={handleConfirmAllowanceDelete}
        title="Delete Record"
        message="Are you sure you want to delete this allowance? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {activeTab === "payments" && (
        <>
          {/* Year filter above Rental Payments */}
          <Box className="condo-unit-detail-header-actions" sx={{ mb: 1 }}>
            <FormControl size="small">
              <InputLabel id="condo-unit-year-label">Year</InputLabel>
              <Select
                labelId="condo-unit-year-label"
                label="Year"
                defaultValue="all"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="2023">2023</MenuItem>
                <MenuItem value="2024">2024</MenuItem>
                <MenuItem value="2025">2025</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Rental Payments */}
          <Paper elevation={0} className="content-box condo-unit-detail-card">
            <DetailTableSection
              id="rental-payments"
              title="Rental Payments"
              manageLabel="View Rental Payment Dues"
              columns={["Acct Entry Date", "Period Covered", "Receipt No", "Payment Date", "RF No", "Check No", "Amount", "Remarks"]}
              onManage={() => {
                if (typeof onNavigate === "function") {
                  onNavigate({ key: "condo-payment" });
                }
              }}
              onRentalEdit={(row) => {
                setSelectedRentalPayment(row);
                setIsRentalPaymentModalOpen(true);
              }}
            />
          </Paper>

          {/* Misc Expense Payments */}
          <Paper elevation={0} className="content-box condo-unit-detail-card">
            <DetailTableSection
              id="misc-payments"
              title="Misc Expense Payments"
              manageLabel="Add Misc Expense Payment"
              columns={["Acct Entry Date", "Period Covered", "Payment Date", "Expense Type", "Amount", "RF No", "Check No", "Payable To", "Condo Company", "Condo Sharing", "Remarks"]}
              onManage={() => {
                setMiscPaymentModalMode("add");
                setSelectedMiscPayment(null);
                setIsMiscPaymentModalOpen(true);
              }}
              onMiscPaymentEdit={(row) => {
                setMiscPaymentModalMode("edit");
                setSelectedMiscPayment(row);
                setIsMiscPaymentModalOpen(true);
              }}
              onMiscPaymentDelete={handleMiscPaymentDelete}
            />
          </Paper>

          {/* Utility Payments */}
          <Paper elevation={0} className="content-box condo-unit-detail-card">
            <DetailTableSection
              id="utility-payments"
              title="Utility Payments"
              manageLabel="Add Utility Payment"
              columns={["Acct Entry Date", "Period Covered", "Payment Date", "Utility Type", "Amount", "RF No", "Check No", "Payable To", "Condo Company", "Condo Sharing", "Remarks"]}
              onManage={() => {
                setUtilityPaymentModalMode("add");
                setSelectedUtilityPayment(null);
                setIsUtilityPaymentModalOpen(true);
              }}
              onUtilityPaymentEdit={(row) => {
                setUtilityPaymentModalMode("edit");
                setSelectedUtilityPayment(row);
                setIsUtilityPaymentModalOpen(true);
              }}
              onUtilityPaymentDelete={handleUtilityPaymentDelete}
            />
          </Paper>

          {/* Maintenance */}
          <Paper elevation={0} className="content-box condo-unit-detail-card">
            <DetailTableSection
              id="maintenance"
              title="Maintenance"
              manageLabel="Manage Maintenance"
              columns={["Date", "Description", "In-Charge", "Total", "Acct Entry Date"]}
              onManage={() => setIsManageMaintenanceModalOpen(true)}
            />
          </Paper>

          {/* Allowance */}
          <Paper elevation={0} className="content-box condo-unit-detail-card">
            <DetailTableSection
              id="allowance"
              title="Allowance"
              manageLabel="Add Allowance"
              columns={["Payment", "Payment To", "Remarks", "Amount", "Condo Company", "Condo Sharing"]}
              onManage={() => {
                setAllowanceModalMode("add");
                setSelectedAllowance(null);
                setIsAllowanceModalOpen(true);
              }}
              onAllowanceEdit={(row) => {
                setAllowanceModalMode("edit");
                setSelectedAllowance(row);
                setIsAllowanceModalOpen(true);
              }}
              onAllowanceDelete={handleAllowanceDelete}
            />
          </Paper>
        </>
      )}
    </PageLayout>
  );
}
